import { useEffect, useMemo, useState } from 'react';
import { useTenant } from '@/hooks/useTenant';
import { useHeroBanners } from '@/hooks/useHeroBanners';
import { heroBannerToItem, isBannerVisible } from '@/lib/heroBanners';
import HeroBannerSlider from '@/components/feature/HeroBannerSlider';

interface HeroBannersTagProps {
  limit?: number;
  /** 1-based : ne garder que la N-ième bannière (ex. index="2"). */
  index?: number;
  /** Id précis d'une bannière à afficher sur une page donnée. */
  banner?: number;
}

/**
 * Balise {{hero_banners}} — affiche les bannières hero (slider) du marchand.
 *
 * Variantes :
 *   {{hero_banners}}                 → toutes les bannières (slider auto)
 *   {{hero_banners limit="1"}}       → la première bannière uniquement (statique)
 *   {{hero_banners index="2"}}       → la 2e bannière uniquement
 *   {{hero_banners banner="12"}}     → la bannière dont l'id est 12
 *
 * Aucune donnée fictive : s'il n'y a rien d'actif, le bloc ne s'affiche pas.
 */
export default function HeroBannersTag({ limit, index, banner }: HeroBannersTagProps) {
  const { tenant, isTenant } = useTenant();
  const merchantId = isTenant && tenant ? Number(tenant.id) : null;
  const { banners, loading } = useHeroBanners(merchantId);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!loading) setReady(true);
  }, [loading]);

  const items = useMemo(() => {
    let visible = banners.filter(isBannerVisible);

    // Bannière précise par id.
    if (banner && banner > 0) {
      visible = visible.filter((b) => b.id === banner);
    }

    // Bannière précise par position (1-based).
    if (index && index > 0) {
      const picked = visible[index - 1];
      visible = picked ? [picked] : [];
    }

    // Limite du nombre d'éléments.
    const limited = limit && limit > 0 ? visible.slice(0, limit) : visible;
    return limited.map(heroBannerToItem);
  }, [banners, limit, index, banner]);

  if (!ready || items.length === 0) return null;

  return <HeroBannerSlider banners={items} />;
}